import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";

const MonthlyOvertimeReport = () => {
  const today = new Date().toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    dept_id: "0",
    overtime_status: "2",
  });

  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= INPUT ================= */
  const handleInput = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= DEPARTMENTS ================= */
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/showdepartment", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      setDepartments(res.data?.departments || res.data?.data || res.data || []);
    } catch {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  /* ================= FETCH REPORT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(
        API_BASE_URL + "/monthly_overtime_report",
        filters,
        { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
      );

      setRecords(res.data.records || []);
      toast.success("Monthly OT Report loaded successfully!", {
        position: "bottom-right",
        theme: "colored",
      });
    } catch {
      toast.error("Error fetching monthly overtime report");
    } finally {
      setLoading(false);
    }
  };

  /* ================= SEARCH ================= */
  const filteredRecords = records.filter((item) =>
    `${item.employee_id} ${item.employee_name} ${item.department} ${item.no_of_hour} ${item.amount}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= COLUMNS ================= */
  const columns = [
    { name: "Employee ID", selector: r => r.employee_id, sortable: true },
    { name: "Employee Name", selector: r => r.employee_name, sortable: true },
    { name: "Department", selector: r => r.department, sortable: true },
    { name: "No of Hours", selector: r => r.no_of_hour, sortable: true, right: true },
    { name: "Calc Point", selector: r => r.calc_point, sortable: true, right: true },
    { name: "Amount", selector: r => r.amount, sortable: true, right: true },
    
  ];

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
      },
    },
  };

  return (
    <div className="container-fluid mt-4">
      <ToastContainer />

      {/* ================= HEADER ================= */}
      <div className="mb-3 d-flex gap-2">
        <a href="/overtime-report" className="btn btn-outline-primary btn-sm">
          Daily Overtime Report
        </a>
        <button className="btn btn-info btn-sm" disabled>
          Monthly Overtime Report
        </button>
      </div>

      {/* ================= FILTER ================= */}
      <div className="card shadow-sm mb-4">
        <div className="card-header">Monthly Overtime Report</div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">

              <div className="col-md-2">
                <label>From Date</label>
                <input type="date" className="form-control"
                  name="start_date" value={filters.start_date}
                  onChange={handleInput} />
              </div>

              <div className="col-md-2">
                <label>To Date</label>
                <input type="date" className="form-control"
                  name="end_date" value={filters.end_date}
                  onChange={handleInput} />
              </div>

              <div className="col-md-3">
                <label>Department</label>
                <Select
                  options={departments.map(d => ({
                    value: d.department_id || d.id,
                    label: d.department_name,
                  }))}
                  isClearable
                  placeholder="All Department"
                  value={departments
                    .map(d => ({
                      value: d.department_id || d.id,
                      label: d.department_name,
                    }))
                    .find(o => o.value === filters.dept_id) || null}
                  onChange={(opt) =>
                    handleInput({
                      target: {
                        name: "dept_id",
                        value: opt ? opt.value : "0",
                      },
                    })
                  }
                />
              </div>

              <div className="col-md-2">
                <label>Status</label>
                <select className="form-control"
                  name="overtime_status"
                  value={filters.overtime_status}
                  onChange={handleInput}>
                  <option value="2">Approved</option>
                  <option value="3">Rejected</option>
                </select>
              </div>

              <div className="col-md-2">
                <button className="btn btn-primary w-100">Get</button>
              </div>

            </div>
          </form>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="card">
        <div className="card-header text-danger">
          Monthly Overtime Summary
        </div>
        <div className="card-body">

          <div className="d-flex justify-content-between mb-3">
            <DownloadButtons
              data={filteredRecords}
              filename="Monthly_OT_Report"
            />

            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              style={{ width: "220px" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <DataTable
            columns={columns}
            data={filteredRecords}
            pagination
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            progressPending={loading}
            striped
            highlightOnHover
            dense
            customStyles={customStyles}
            noDataComponent={
              <div className="p-3 text-danger fw-bold">
                No records found
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MonthlyOvertimeReport;
